import { useState, useEffect, useMemo, useRef } from "react";
import { useTherapist } from "../../../../../context/useTherapist";
import { groupLabel, type GroupPatient } from "../../../../patient";
import { listTherapistPatients, listTherapistCouplePatients, listGroupPatients } from "../../../application/sessionPatients";
import { listSessionTemplates } from "../../../application/sessionTemplates";
import type { Patient, CouplePatient } from "../../../../patient";
import { Video, MapPin, Search, Users, User } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../../shared/ui/icons/SubjectKindIcon";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import DatePicker from "../../../../../shared/ui/form/date-picker";
import Input from "../../../../../shared/ui/form/input/InputField";
import Label from "../../../../../shared/ui/form/Label";
import Button from "../../../../../shared/ui/button/Button";
import { THERAPY_TYPES } from "../../../../../shared/domain/therapyTypes";
import FilterSelect from "../../../../../shared/ui/form/FilterSelect";
import PillSelector from "../../../../../shared/ui/form/PillSelector";
import { TemplateSelector } from "../../../../session_templates";
import { useCourseLabels, useSessionPreferences, type SessionMode } from "../../../../center";

interface SessionBasicFormData {
  date: string;
  title: string;
  patient: string; // This is patient_id
  couple_patient_id?: string;
  group_patient_id?: string;
  therapy_type: string;
  hour: string;
  ubication: string;
  type: string; // 'individual', 'couple' or 'group'
  duration: number;
  template_id?: string;
}

const MODES: SessionMode[] = ["individual", "couple", "group"];

const MODE_ICONS = { individual: User, couple: Users, group: GroupOfPeopleIcon } as const;

/** Switching mode clears whoever was picked under the other two. */
const MODE_CHANGES: Record<SessionMode, Partial<SessionBasicFormData>> = {
  individual: { type: "individual", couple_patient_id: undefined, group_patient_id: undefined },
  couple: { type: "couple", patient: undefined, group_patient_id: undefined },
  group: { type: "group", patient: undefined, couple_patient_id: undefined },
};

interface SessionBasicFormProps {
  therapistID: string;
  onChange?: (data: Partial<SessionBasicFormData>) => void;
  onContinue?: () => void;
  data: SessionBasicFormData;
}

export default function SessionAddBasicForm({ therapistID, onChange, onContinue, data }: SessionBasicFormProps) {
  const { therapist } = useTherapist();
  const { t } = useTranslation();
  // A training centre's student is named with the course they are on.
  const { labelOf } = useCourseLabels();
  // A centre may leave the clinical fields out; the first template still applies.
  const { therapyFieldsEnabled } = useSessionPreferences();
  const [searchParams] = useSearchParams();
  const preselectedPatient = searchParams.get("patient") ?? "";
  const preselectedCouple = searchParams.get("couple") ?? "";
  const preselectedGroup = searchParams.get("group") ?? "";

  const { title, date, patient, couple_patient_id, group_patient_id, therapy_type: therapyTypesStr, hour, ubication, duration, template_id, type } = data;
  // Whoever the session is with, whichever kind of subject that is.
  const subjectId = type === "group" ? group_patient_id : type === "couple" ? couple_patient_id : patient;

  // Only the kinds of session the centre runs are offered. A mode switched off
  // while it is the form's default moves the form to the first one left.
  const { modes } = useSessionPreferences();
  const enabledModes = MODES.filter((mode) => modes[mode]);
  useEffect(() => {
    if (enabledModes.length > 0 && !enabledModes.includes(type as SessionMode)) {
      emitChange(MODE_CHANGES[enabledModes[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, modes.individual, modes.couple, modes.group]);

  const therapyTypes = therapyTypesStr ? therapyTypesStr.split(", ").filter(Boolean) : [];

  // Pre-fill from what the therapist declared they practise, once, and only
  // into an empty field: a therapist who cleared it meant to clear it.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || therapyTypesStr || !therapyFieldsEnabled) return;
    const declared = therapist?.therapy_types ?? [];
    if (declared.length === 0) return;
    prefilled.current = true;
    emitChange({ therapy_type: declared.join(", ") });
    // `emitChange` is stable for the caller's lifetime; re-running on it would
    // fight the therapist's own edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapist, therapyTypesStr, therapyFieldsEnabled]);

  // `name` is the person alone, for the title; `label` may carry their course.
  const [patients, setPatients] = useState<{ value: string; label: string; name?: string }[]>([]);
  const [couples, setCouples] = useState<{ value: string; label: string }[]>([]);
  const [groups, setGroups] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preselectedPatient && !patient) {
      emitChange({ patient: preselectedPatient, type: "individual" });
    }
    if (preselectedCouple && !couple_patient_id) {
      emitChange({ couple_patient_id: preselectedCouple, type: "couple" });
    }
    if (preselectedGroup && !group_patient_id) {
      emitChange({ group_patient_id: preselectedGroup, type: "group" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedPatient, preselectedCouple, preselectedGroup]);

  const optionsFor = (kind: string): { value: string; label: string; name?: string }[] =>
    kind === "group" ? groups : kind === "couple" ? couples : patients;

  // 🔹 Auto-fill title when patient/couple/group is loaded
  useEffect(() => {
    if (!title && subjectId) {
      const selected = optionsFor(type).find((o) => o.value === subjectId);
      if (selected) {
        emitChange({ title: `${date} - ${selected.name ?? selected.label}` });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patients, couples, groups, subjectId, title, date, type]);

  useEffect(() => {
    if (!therapistID) return;
    setLoading(true);

    const fetchPatients = listTherapistPatients(therapistID);
    const fetchCouples = listTherapistCouplePatients(therapistID);
    // A therapist with no groups, or a backend without them, still books the rest.
    const fetchGroups = listGroupPatients().catch(() => [] as GroupPatient[]);

    Promise.all([fetchPatients, fetchCouples, fetchGroups])
      .then(([pRes, cRes, gRes]) => {
        const pData = pRes.patients || [];
        setPatients(pData.map((p: Patient) => ({
          value: p.id,
          label: labelOf(p),
          name: `${p.first_name} ${p.last_name}`,
        })));

        const cData = cRes || [];
        setCouples(cData.map((c: CouplePatient) => ({
          value: c.id,
          label: c.display_name,
        })));

        setGroups((gRes || []).map((g: GroupPatient) => ({
          value: g.id,
          label: groupLabel(g),
        })));
      })
      .catch((err: unknown) => {
        console.error("Error fetching data:", err);
      })
      .finally(() => setLoading(false));
  }, [therapistID, t, labelOf]);

  const emitChange = (newValues: Partial<SessionBasicFormData>) => {
    onChange?.(newValues);
  };

  useEffect(() => {
    if (template_id) return;
    listSessionTemplates().then(templates => {
      if (templates.length > 0 && templates[0].id) {
        emitChange({ template_id: templates[0].id });
      }
    }).catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isValid = Boolean(
    subjectId &&
    title &&
    date &&
    hour &&
    duration &&
    ubication &&
    template_id
  );

  const activeOptions = useMemo((): { value: string; label: string; name?: string }[] => {
    return type === "group" ? groups : type === "couple" ? couples : patients;
  }, [type, patients, couples, groups]);

  return (
    <ComponentCard title={t("session.basicInfo")}>
      <div className="space-y-8">

        {/* Toggle between Individual, Couple and Group */}
        <div>
          <Label className="mb-3 uppercase text-xs font-semibold text-content-tertiary tracking-wider">
            {t("session.sessionType")}
          </Label>
          <div className="flex bg-gray-100 p-1 rounded-xl w-full max-w-sm">
            {enabledModes.map((mode) => {
              const Icon = MODE_ICONS[mode];
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => emitChange(MODE_CHANGES[mode])}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${type === mode ? 'bg-white text-accent-text shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{t(`session.typeOptions.${mode}`)}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {/* Patient or Couple Selection */}
            <div>
              <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider">
                {type === "group" ? t("patient.group.eyebrow") : type === "couple" ? t("patient.coupleSession") : t("session.patient")}
              </Label>
              <FilterSelect
                options={activeOptions}
                value={subjectId || ""}
                onChange={(value) => {
                  const val = value as string;
                  const selected = activeOptions.find((o) => o.value === val);
                  const changes: Partial<SessionBasicFormData> = {};

                  if (type === "group") {
                    changes.group_patient_id = val || undefined;
                  } else if (type === "couple") {
                    changes.couple_patient_id = val || undefined;
                  } else {
                    changes.patient = val || undefined;
                  }

                  if (selected && !title) {
                    changes.title = `${date} - ${selected.name ?? selected.label}`;
                  }

                  emitChange(changes);
                }}
                placeholder={loading ? t("common.loading") : (type === "individual" ? t("session.selectPatient") : t("patient.selectPatient"))}
                searchPlaceholder={t("patient.searchPlaceholder")}
                className="w-full"
              />
            </div>

            {/* Session Title */}
            <div>
              <Label htmlFor="title" className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.title")}</Label>
              <Input
                type="text"
                id="title"
                value={title}
                placeholder={t("session.titlePlaceholder")}
                hint={t("session.titlePlaceholder")}
                className="rounded-xl bg-gray-50 border-gray-200 focus:bg-white transition-all py-3"
                onChange={(e) => {
                  emitChange({ title: e.target.value });
                }}
              />
            </div>
          </div>
        </div>

        {/* Date & Time */}
        <div>
          <Label className="mb-3 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.dateTime")}</Label>
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-100">
              <DatePicker
                id="date-picker"
                label=""
                placeholder={t("session.selectDate")}
                defaultDate={date}
                onChange={(_, currentDateString) => {
                  emitChange({ date: currentDateString });
                }}
                className="border-none shadow-none focus-within:ring-0"
              />
            </div>
            <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-100">
              <div className="relative">
                <DatePicker
                  id="time-picker"
                  mode="time"
                  placeholder={t("session.time")}
                  defaultDate={hour}
                  onChange={(_, currentTimeString) => {
                    emitChange({ hour: currentTimeString });
                  }}
                  className="border-none shadow-none focus-within:ring-0"
                  calendarClassName="time-picker-lg"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Duration & Location */}
        <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <div>
            <Label className="mb-3 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.duration")}</Label>
            <PillSelector
              options={[
                { value: 30, label: '30m' },
                { value: 50, label: '50m' },
                { value: 60, label: '60m' },
                { value: 90, label: '90m' },
              ]}
              value={duration}
              onChange={(val) => emitChange({ duration: Number(val) })}
            />
          </div>
          <div>
            <Label className="mb-3 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.location")}</Label>
            <div className="flex bg-gray-100 p-1 rounded-xl">
              <button
                type="button"
                onClick={() => emitChange({ ubication: 'online' })}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${ubication === 'online' ? 'bg-white text-accent-text shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
              >
                <Video className="w-4 h-4" />
                <span>{t("session.locationOptions.online")}</span>
              </button>
              <button
                type="button"
                onClick={() => emitChange({ ubication: 'in person' })}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${ubication === 'in person' ? 'bg-white text-accent-text shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
              >
                <MapPin className="w-4 h-4" />
                <span>{t("session.locationOptions.in_person")}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Template & Therapy type */}
        {therapyFieldsEnabled && (
        <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <div>
            <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider">
              {t("templates.templateSelect", "Session Template")}
            </Label>
            <div className={`bg-white rounded-lg shadow-sm ${!template_id ? 'ring-1 ring-red-200 rounded-lg' : ''}`}>
              <TemplateSelector
                value={template_id || ""}
                onChange={(val) => emitChange({ template_id: val || undefined })}
                className="w-full"
              />
            </div>
            {!template_id ? (
              <p className="text-xs text-red-400 mt-1">{t("templates.templateRequired")}</p>
            ) : (
              <p className="text-xs text-content-tertiary mt-1">{t("templates.templateHint")}</p>
            )}
          </div>

          <div className="col-span-1 md:col-span-2">
            <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider">{t("session.therapyType")} ({t("common.optional")})</Label>
            <FilterSelect
              options={THERAPY_TYPES.map((type) => ({
                value: type,
                label: t(`session.therapyOptions.${type}`),
              }))}
              value={therapyTypes}
              onChange={(val) => {
                const arr = Array.isArray(val) ? val : [val];
                emitChange({ therapy_type: arr.join(", ") });
              }}
              placeholder={t("session.therapyType")}
              searchPlaceholder={t("common.search")}
              icon={<Search className="w-5 h-5" />}
              multiple={true}
              allowCustomValues={true}
              addCustomLabel={t("common.add")}
            />
          </div>
        </div>
        )}

        <div className="col-span-full flex flex-col items-center pt-6 border-t border-gray-100">
          <Button
            onClick={onContinue}
            className={`w-full md:w-auto px-8 py-3 rounded-xl shadow-lg shadow-raised ${!isValid ? 'opacity-50 cursor-not-allowed' : ''}`}
            size="md"
            disabled={!isValid}
          >
            {t("session.continue")}
          </Button>
        </div>
      </div>
    </ComponentCard>
  );
}
